import React, { useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '@/store/store'
import { toggleMobileMenu } from '@/store/slices/uiSlice'
// @ts-ignore: keeping for future development use
import { logoutUser } from '@/store/slices/authSlice'
import { FiMenu, FiBell, FiSearch, FiUser, FiStar } from 'react-icons/fi'
import NotificationDropdown from '@/components/notifications/NotificationDropdown'
import ProfileDropdown from '@/components/layout/ProfileDropdown'
import SearchModal from '@/components/ui/SearchModal'
import SearchDropdown from '@/components/ui/SearchDropdown'
import Logo from '@/components/ui/Logo'
import Avatar from '@/components/ui/Avatar'

const Header: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const location = useLocation()
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth)
    // @ts-ignore: keeping for future development use
    const { mobileMenuOpen: _mobileMenuOpen } = useSelector((state: RootState) => state.ui)
    const { unreadCount } = useSelector((state: RootState) => state.notifications)

    // Notification dropdown state
    const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false)
    const notificationButtonRef = useRef<HTMLButtonElement>(null)

    // Profile dropdown state
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
    const profileButtonRef = useRef<HTMLDivElement>(null)

    // Search state
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const showSidebarMenuButton = location.pathname !== '/'

    return (
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Left Section */}
                    <div className="flex items-center">
                        {/* Mobile menu button */}
                        {showSidebarMenuButton && (
                            <button
                                onClick={() => dispatch(toggleMobileMenu())}
                                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                title="Open menu"
                            >
                                <FiMenu className="h-6 w-6" />
                            </button>
                        )}



                        {/* Logo */}
                        <Link
                            to="/"
                            className="flex items-center"
                        >
                            <div className="flex-shrink-0">
                                <Logo
                                    size="md"
                                    showText={true}
                                    className="transition-opacity hover:opacity-80"
                                />
                            </div>
                        </Link>
                    </div>

                    {/* Center Section - Search */}
                    <div className="hidden md:block flex-1 max-w-2xl mx-8">
                        <SearchDropdown
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search gigs..."
                        />
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center space-x-4">
                        {/* Search button for mobile */}
                        <button
                            onClick={() => setIsSearchModalOpen(true)}
                            className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Search"
                        >
                            <FiSearch className="h-6 w-6" />
                        </button>

                        {isAuthenticated ? (
                            <>
                                {/* Share Story Button */}
                                <button
                                    onClick={() => navigate('/success-stories/submit')}
                                    className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-label text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                                >
                                    <FiStar className="h-5 w-5" />
                                    <span>Share Story</span>
                                </button>

                                {/* Notifications */}
                                <div className="relative">
                                    <button
                                        ref={notificationButtonRef}
                                        onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)}
                                        className={`relative p-2 rounded-md transition-colors ${isNotificationDropdownOpen
                                            ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400'
                                            : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        <FiBell className="h-6 w-6" />
                                        {unreadCount > 0 && (
                                            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                                                {unreadCount > 9 ? '9+' : unreadCount}
                                            </span>
                                        )}
                                    </button>

                                    <NotificationDropdown
                                        isOpen={isNotificationDropdownOpen}
                                        onClose={() => setIsNotificationDropdownOpen(false)}
                                        buttonRef={notificationButtonRef}
                                    />
                                </div>

                                {/* User Menu */}
                                <div className="relative">
                                    <div
                                        ref={profileButtonRef}
                                        className={`flex items-center space-x-3 cursor-pointer p-2 rounded-lg transition-colors ${isProfileDropdownOpen
                                            ? 'bg-primary-50 dark:bg-primary-900/20'
                                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                                            }`}
                                        onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                    >
                                        <div className="hidden md:block text-right">
                                            <p className="text-label text-gray-900 dark:text-white">
                                                {user?.profile.firstName} {user?.profile.lastName}
                                            </p>
                                            <p className="text-caption capitalize">
                                                {user?.role}
                                            </p>
                                        </div>
                                        <Avatar
                                            avatarId={user?.profile.avatar}
                                            alt={`${user?.profile.firstName} ${user?.profile.lastName}`}
                                            size="sm"
                                            className="bg-primary-100 dark:bg-primary-900"
                                            fallbackIcon={<FiUser className="h-5 w-5 text-primary-600 dark:text-primary-400" />}
                                        />
                                    </div>

                                    <ProfileDropdown
                                        isOpen={isProfileDropdownOpen}
                                        onClose={() => setIsProfileDropdownOpen(false)}
                                        buttonRef={profileButtonRef}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center space-x-2">
                                <Link
                                    to="/login"
                                    className="px-3 py-2 text-label text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                >
                                    Sign in
                                </Link>
                                {/* Hide "Get Started" button on small screens */}
                                <Link
                                    to="/register"
                                    className="hidden sm:inline-flex btn-primary"
                                >
                                    Get Started
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Search Modal for Mobile */}
            <SearchModal
                isOpen={isSearchModalOpen}
                onClose={() => setIsSearchModalOpen(false)}
            />
        </header>
    )
}

export default Header